import React, { useState, useEffect } from 'react';
import { Trophy, Flame, BookOpen, CheckCircle, Target, Star, Award, Crown, Zap, Heart, X, Share2 } from 'lucide-react';
import { UserProfile } from '../types';

interface Badge {
  id: string;
  name: string;
  nameHi: string;
  description: string;
  descriptionHi: string;
  icon: React.ReactNode;
  category: 'streak' | 'count' | 'reader' | 'test' | 'special';
  requirement: number;
  checkProgress: (user: UserProfile, extraData?: any) => number;
  color: string;
  bgColor: string;
}

const BADGES: Badge[] = [
  // Streak Badges
  {
    id: 'streak_7',
    name: '7-Day Warrior',
    nameHi: 'सप्ताह वीर',
    description: 'Complete 7 consecutive days of Jaap',
    descriptionHi: '7 दिन लगातार जाप करें',
    icon: <Flame size={24} />,
    category: 'streak',
    requirement: 7,
    checkProgress: (user) => user.streak_days || 0,
    color: 'text-orange-500',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30'
  },
  {
    id: 'streak_30',
    name: '30-Day Sadhak',
    nameHi: 'मासिक साधक',
    description: 'Complete 30 consecutive days of Jaap',
    descriptionHi: '30 दिन लगातार जाप करें',
    icon: <Flame size={24} />,
    category: 'streak',
    requirement: 30,
    checkProgress: (user) => user.streak_days || 0,
    color: 'text-red-500',
    bgColor: 'bg-red-100 dark:bg-red-900/30'
  },
  {
    id: 'streak_108',
    name: '108-Day Master',
    nameHi: 'अष्टोत्तर शतक',
    description: 'Complete 108 consecutive days of Jaap',
    descriptionHi: '108 दिन लगातार जाप करें',
    icon: <Crown size={24} />,
    category: 'streak',
    requirement: 108,
    checkProgress: (user) => user.streak_days || 0,
    color: 'text-purple-500',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30'
  },
  
  // Count Badges
  {
    id: 'count_1k',
    name: 'Thousand Names',
    nameHi: 'सहस्र नाम',
    description: 'Complete 1,000 Ram Naam',
    descriptionHi: '1,000 राम नाम पूर्ण करें',
    icon: <Target size={24} />,
    category: 'count',
    requirement: 1000,
    checkProgress: (user) => user.total_count || 0,
    color: 'text-blue-500',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30'
  },
  {
    id: 'count_10k',
    name: 'Ten Thousand',
    nameHi: 'दस सहस्र',
    description: 'Complete 10,000 Ram Naam',
    descriptionHi: '10,000 राम नाम पूर्ण करें',
    icon: <Star size={24} />,
    category: 'count',
    requirement: 10000,
    checkProgress: (user) => user.total_count || 0,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30'
  },
  {
    id: 'count_1lakh',
    name: 'Lakh Jaapi',
    nameHi: 'लाख जापी',
    description: 'Complete 1,00,000 Ram Naam',
    descriptionHi: '1 लाख राम नाम पूर्ण करें',
    icon: <Award size={24} />,
    category: 'count',
    requirement: 100000,
    checkProgress: (user) => user.total_count || 0,
    color: 'text-amber-500',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30'
  },
  {
    id: 'count_10lakh',
    name: 'Das Lakh Siddhi',
    nameHi: 'दस लाख सिद्धि',
    description: 'Complete 10,00,000 Ram Naam',
    descriptionHi: '10 लाख राम नाम पूर्ण करें',
    icon: <Trophy size={24} />,
    category: 'count',
    requirement: 1000000,
    checkProgress: (user) => user.total_count || 0,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-100 dark:bg-emerald-900/30'
  },
  
  // Reader Badges
  {
    id: 'reader_5',
    name: 'Bookworm',
    nameHi: 'पुस्तक प्रेमी',
    description: 'Read 5 ebooks',
    descriptionHi: '5 पुस्तकें पढ़ें',
    icon: <BookOpen size={24} />,
    category: 'reader',
    requirement: 5,
    checkProgress: (user) => (user.ebook_progress || []).filter(p => p.completedAt).length,
    color: 'text-teal-500',
    bgColor: 'bg-teal-100 dark:bg-teal-900/30'
  },
  {
    id: 'reader_10',
    name: 'Scholar',
    nameHi: 'विद्वान',
    description: 'Read 10 ebooks',
    descriptionHi: '10 पुस्तकें पढ़ें',
    icon: <BookOpen size={24} />,
    category: 'reader',
    requirement: 10,
    checkProgress: (user) => (user.ebook_progress || []).filter(p => p.completedAt).length,
    color: 'text-indigo-500',
    bgColor: 'bg-indigo-100 dark:bg-indigo-900/30'
  },
  
  // Self-Test Badges
  {
    id: 'test_7',
    name: 'Weekly Tester',
    nameHi: 'साप्ताहिक परीक्षक',
    description: 'Complete self-test for 7 days',
    descriptionHi: '7 दिन आत्म-परीक्षण करें',
    icon: <CheckCircle size={24} />,
    category: 'test',
    requirement: 7,
    checkProgress: (_, extraData) => extraData?.testStreak || 0,
    color: 'text-green-500',
    bgColor: 'bg-green-100 dark:bg-green-900/30'
  },
  {
    id: 'test_30',
    name: 'Monthly Evaluator',
    nameHi: 'मासिक मूल्यांकक',
    description: 'Complete self-test for 30 days',
    descriptionHi: '30 दिन आत्म-परीक्षण करें',
    icon: <CheckCircle size={24} />,
    category: 'test',
    requirement: 30,
    checkProgress: (_, extraData) => extraData?.testStreak || 0,
    color: 'text-lime-500',
    bgColor: 'bg-lime-100 dark:bg-lime-900/30'
  },
  
  // Special Badges
  {
    id: 'special_first_jaap',
    name: 'First Step',
    nameHi: 'प्रथम पग',
    description: 'Complete your first Jaap',
    descriptionHi: 'पहला जाप पूर्ण करें',
    icon: <Heart size={24} />,
    category: 'special',
    requirement: 1,
    checkProgress: (user) => user.total_count > 0 ? 1 : 0,
    color: 'text-pink-500',
    bgColor: 'bg-pink-100 dark:bg-pink-900/30'
  },
  {
    id: 'special_mala',
    name: 'First Mala',
    nameHi: 'प्रथम माला',
    description: 'Complete 108 Jaap in one session',
    descriptionHi: 'एक सत्र में 108 जाप करें',
    icon: <Zap size={24} />,
    category: 'special',
    requirement: 108,
    checkProgress: (_, extraData) => extraData?.singleSessionMax || 0,
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-100 dark:bg-cyan-900/30'
  }
];

interface AchievementBadgesProps {
  user: UserProfile;
  isOpen: boolean;
  onClose: () => void;
}

export const AchievementBadges: React.FC<AchievementBadgesProps> = ({ user, isOpen, onClose }) => {
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);
  const [extraData, setExtraData] = useState<any>({});
  
  useEffect(() => {
    // Load extra data from localStorage
    const testEntries = JSON.parse(localStorage.getItem('sadhak_daily_entries') || '[]');
    const testStreak = calculateTestStreak(testEntries);
    const singleSessionMax = parseInt(localStorage.getItem('ram_naam_single_session_max') || '0');
    
    setExtraData({ testStreak, singleSessionMax });
  }, [isOpen]);
  
  const calculateTestStreak = (entries: any[]) => {
    if (!entries.length) return 0;
    
    const sortedDates = Array.from(new Set(entries.map((e: any) => e.date))).sort().reverse();
    let streak = 0;
    const today = new Date().toISOString().split('T')[0];
    
    for (let i = 0; i < sortedDates.length; i++) {
      const expectedDate = new Date();
      expectedDate.setDate(expectedDate.getDate() - i);
      const expected = expectedDate.toISOString().split('T')[0];
      
      if (sortedDates[i] === expected || (i === 0 && sortedDates[i] === today)) {
        streak++;
      } else {
        break;
      }
    }
    return streak;
  };
  
  const getBadgeStatus = (badge: Badge) => {
    const progress = badge.checkProgress(user, extraData);
    const isUnlocked = progress >= badge.requirement;
    const percentage = Math.min((progress / badge.requirement) * 100, 100);
    return { isUnlocked, progress, percentage };
  };
  
  const unlockedBadges = BADGES.filter(b => getBadgeStatus(b).isUnlocked);
  const lockedBadges = BADGES.filter(b => !getBadgeStatus(b).isUnlocked);
  
  const handleShare = async (badge: Badge) => {
    const shareText = `🏆 मैंने "${badge.nameHi}" बैज प्राप्त किया!\n\n${badge.descriptionHi}\n\nगोलोक दिव्य नाम कोष में जाप करें 🙏`;
    
    if (navigator.share) {
      try {
        await navigator.share({ title: badge.nameHi, text: shareText });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(shareText);
      alert('Copied to clipboard!');
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="badges-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-2xl max-h-[85vh] bg-white dark:bg-stone-900 rounded-3xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-5 text-center relative">
          <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/20">
            <X size={20} className="text-white" />
          </button>
          <Trophy className="mx-auto mb-2 text-white" size={32} />
          <h2 className="text-xl font-bold text-white">उपलब्धि बैज</h2>
          <p className="text-white/80 text-sm">Achievement Badges</p>
          <div className="mt-2 text-white/90 text-sm">
            {unlockedBadges.length} / {BADGES.length} अनलॉक
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Unlocked Badges */}
          {unlockedBadges.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-2">
                <Star size={16} className="text-yellow-500" /> प्राप्त बैज ({unlockedBadges.length})
              </h3>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {unlockedBadges.map(badge => (
                  <button
                    key={badge.id}
                    onClick={() => setSelectedBadge(badge)}
                    className={`${badge.bgColor} p-3 rounded-2xl flex flex-col items-center gap-2 transition-transform hover:scale-105 border-2 border-transparent hover:border-amber-400`}
                    data-testid={`badge-${badge.id}`}
                  >
                    <div className={badge.color}>{badge.icon}</div>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-200 text-center line-clamp-2">
                      {badge.nameHi}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Locked Badges */}
          {lockedBadges.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3">
                अप्राप्त बैज ({lockedBadges.length})
              </h3>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {lockedBadges.map(badge => {
                  const { progress, percentage } = getBadgeStatus(badge);
                  return (
                    <button
                      key={badge.id}
                      onClick={() => setSelectedBadge(badge)}
                      className="bg-gray-100 dark:bg-stone-800 p-3 rounded-2xl flex flex-col items-center gap-2 relative overflow-hidden opacity-60 hover:opacity-80 transition-opacity"
                    >
                      {/* Progress bar */}
                      <div 
                        className="absolute bottom-0 left-0 h-1 bg-amber-400 transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                      <div className="text-gray-400 dark:text-gray-500">{badge.icon}</div>
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 text-center line-clamp-2">
                        {badge.nameHi}
                      </span>
                      <span className="text-[10px] text-gray-400">
                        {progress.toLocaleString()} / {badge.requirement.toLocaleString()}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        
        {/* Badge Detail Modal */}
        {selectedBadge && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-10" onClick={() => setSelectedBadge(null)}>
            <div className="bg-white dark:bg-stone-800 rounded-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
              <div className={`${selectedBadge.bgColor} w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4`}>
                <div className={selectedBadge.color}>{React.cloneElement(selectedBadge.icon as React.ReactElement<any>, { size: 40 })}</div>
              </div>
              <h3 className="text-xl font-bold text-center text-gray-800 dark:text-white mb-1">
                {selectedBadge.nameHi}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-3">
                {selectedBadge.name}
              </p>
              <p className="text-center text-gray-600 dark:text-gray-300 mb-4">
                {selectedBadge.descriptionHi}
              </p>
              
              {getBadgeStatus(selectedBadge).isUnlocked ? (
                <div className="space-y-3">
                  <div className="text-center text-green-500 font-medium flex items-center justify-center gap-2">
                    <CheckCircle size={20} /> अनलॉक!
                  </div>
                  <button
                    onClick={() => handleShare(selectedBadge)}
                    className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl flex items-center justify-center gap-2 font-medium"
                  >
                    <Share2 size={18} /> शेयर करें
                  </button>
                </div>
              ) : (
                <div>
                  <div className="bg-gray-100 dark:bg-stone-700 rounded-full h-3 overflow-hidden mb-2">
                    <div 
                      className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all"
                      style={{ width: `${getBadgeStatus(selectedBadge).percentage}%` }}
                    />
                  </div>
                  <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                    {getBadgeStatus(selectedBadge).progress.toLocaleString()} / {selectedBadge.requirement.toLocaleString()}
                  </p>
                </div>
              )}
              
              <button
                onClick={() => setSelectedBadge(null)}
                className="w-full mt-4 py-2 text-gray-500 dark:text-gray-400 text-sm"
              >
                बंद करें
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Mini badge display for dashboard
export const BadgeShowcase: React.FC<{ user: UserProfile; onClick: () => void }> = ({ user, onClick }) => {
  const [extraData, setExtraData] = useState<any>({});
  
  useEffect(() => {
    const testEntries = JSON.parse(localStorage.getItem('sadhak_daily_entries') || '[]');
    const sortedDates = Array.from(new Set(testEntries.map((e: any) => e.date))).sort().reverse();
    let testStreak = 0;
    for (let i = 0; i < sortedDates.length; i++) {
      const expectedDate = new Date();
      expectedDate.setDate(expectedDate.getDate() - i);
      if (sortedDates[i] === expectedDate.toISOString().split('T')[0]) testStreak++;
      else break;
    }
    setExtraData({ testStreak, singleSessionMax: parseInt(localStorage.getItem('ram_naam_single_session_max') || '0') });
  }, []);
  
  const unlockedBadges = BADGES.filter(b => {
    const progress = b.checkProgress(user, extraData);
    return progress >= b.requirement;
  });
  
  const recentBadges = unlockedBadges.slice(0, 4);
  
  return (
    <button
      onClick={onClick}
      className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-2xl p-4 w-full text-left hover:shadow-md transition-shadow"
      data-testid="badge-showcase"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Trophy size={18} className="text-amber-500" />
          <span className="font-semibold text-gray-800 dark:text-gray-200">मेरे बैज</span>
        </div>
        <span className="text-sm text-amber-600 dark:text-amber-400">
          {unlockedBadges.length}/{BADGES.length}
        </span>
      </div>
      
      <div className="flex gap-2">
        {recentBadges.length > 0 ? (
          recentBadges.map(badge => (
            <div key={badge.id} className={`${badge.bgColor} p-2 rounded-xl`}>
              <div className={badge.color}>{React.cloneElement(badge.icon as React.ReactElement<any>, { size: 20 })}</div>
            </div>
          ))
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">जाप शुरू करें और बैज प्राप्त करें!</p>
        )}
        {unlockedBadges.length > 4 && (
          <div className="bg-gray-100 dark:bg-stone-700 p-2 rounded-xl flex items-center justify-center">
            <span className="text-xs text-gray-500 dark:text-gray-400">+{unlockedBadges.length - 4}</span>
          </div>
        )}
      </div>
    </button>
  );
};

export default AchievementBadges;
